import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { PartnerMembershipService } from '@sisoma/auth';
import { EmptyState, EmptyStateBody, LinkButton } from '@sisoma/ui';

import { PartnersData } from '../../core/data/partners.data';

/**
 * Shown to someone who holds a seat in this partner area but has no role on it.
 *
 * A partner area is seeded with the Owner role alone; every other role is
 * created by the owner, typically to mirror their Entra app roles. Until
 * someone is given one of those, they have an active seat and an empty
 * capability set — they can sign in, they appear in the members list, they
 * consume a licence, and there is nothing in the app they can open.
 *
 * Without this screen that state renders as the app itself: a sidenav stripped
 * of every gated entry and the static dashboard behind it, with nothing saying
 * why. The distinction that matters is against a *suspended* seat — same empty
 * capability set, opposite meaning ("wait for a role" versus "someone took your
 * access away") — which is why the two have separate screens rather than one
 * shared "no access" page.
 *
 * The partner switcher stays in the sidenav footer while this renders, so
 * someone who works in several partner areas can leave for one they do have a
 * role in.
 */
@Component({
  selector: 'app-no-role',
  imports: [EmptyState, EmptyStateBody, LinkButton],
  templateUrl: './no-role.html',
  styleUrl: './no-role.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NoRole {
  private readonly membership = inject(PartnerMembershipService);
  private readonly partners = inject(PartnersData);

  /**
   * The area's name, or a generic stand-in. Off `GET /v1/me/capabilities`,
   * which is already scoped to the active partner and already fetched on every
   * switch — so the fallback covers a `null` from the API rather than a list
   * still in flight.
   */
  protected readonly partnerName = computed(
    () => this.membership.partnerName() ?? 'this partner area',
  );

  /** Who to ask. Absent when the area has no resolvable owner. */
  protected readonly ownerEmail = this.membership.ownerEmail;

  /**
   * Whether pointing at the switcher would actually get them anywhere.
   *
   * The one thing here that still needs the list. `/v1/me/capabilities` is
   * scoped to a single partner by construction, so it cannot answer a question
   * about the others.
   */
  protected readonly hasOtherPartners = computed(
    () => (this.partners.partners.value() ?? []).length > 1,
  );
}
